/**
 * Zona 3: as folhas de verdade (mesmo componente da impressão), em escala,
 * agrupadas por lote, com os avisos do lote em cima. Clique abre a folha em
 * tamanho real com ← →. As miniaturas só desenham quando entram na tela.
 */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";

import Folha from "./folha/Folha";
import { LARGURA_PAISAGEM, ALTURA_PAISAGEM } from "./folha/styles";
import { Bloco, GradeDeMiniaturas, GrupoDoLote, LegendaDaMiniatura, ListaDeAvisos, Miniatura, Modal, Previa, Vazio } from "./styles";

function tamanhoDaFolha(folha) {
  return folha.setor.retrato
    ? { largura: ALTURA_PAISAGEM, altura: LARGURA_PAISAGEM }
    : { largura: LARGURA_PAISAGEM, altura: ALTURA_PAISAGEM };
}

function useLarguraEVisivel() {
  const ref = useRef(null);
  const [largura, setLargura] = useState(0);
  const [visivel, setVisivel] = useState(false);

  useLayoutEffect(() => {
    const elemento = ref.current;
    if (!elemento) return undefined;
    setLargura(elemento.clientWidth);
    const redimensionar = typeof ResizeObserver === "function"
      ? new ResizeObserver(([entrada]) => setLargura(entrada.contentRect.width))
      : null;
    redimensionar?.observe(elemento);
    const observar = typeof IntersectionObserver === "function"
      ? new IntersectionObserver(([entrada]) => {
        if (entrada.isIntersecting) setVisivel(true);
      }, { rootMargin: "400px" })
      : null;
    if (observar) observar.observe(elemento);
    else setVisivel(true);
    return () => {
      redimensionar?.disconnect();
      observar?.disconnect();
    };
  }, []);

  return { ref, largura, visivel };
}

/** Folha escalada para caber na largura disponível. */
export function FolhaEscalada({ folha, logo, via, celulasComAviso, larguraMaxima, ajuste }) {
  const { ref, largura, visivel } = useLarguraEVisivel();
  const { largura: larguraDaFolha, altura } = tamanhoDaFolha(folha);
  const disponivel = larguraMaxima ? Math.min(largura, larguraMaxima) : largura;
  const escala = disponivel ? disponivel / larguraDaFolha : 0;
  return (
    <div ref={ref} style={{ width: "100%" }} aria-hidden="true">
      <div className="moldura" style={{ position: "relative", overflow: "hidden", width: disponivel || "100%", height: altura * escala, margin: "0 auto", background: "#fff" }}>
        {visivel && escala > 0 && (
          <div className="escala" style={{ position: "absolute", top: 0, left: 0, transformOrigin: "top left", transform: `scale(${escala})` }}>
            <Folha folha={folha} logo={logo} via={via} celulasComAviso={celulasComAviso} ajuste={ajuste} />
          </div>
        )}
      </div>
    </div>
  );
}

function rotuloDaFolha(folha) {
  return folha.setor.curto;
}

function FolhaAmpliada({ folhas, indice, onIndice, onFechar, logo, vias, celulasComAviso, ajusteDe }) {
  const folha = folhas[indice];
  const fechar = useRef(null);

  useEffect(() => {
    fechar.current?.focus();
    const tecla = (evento) => {
      if (evento.key === "Escape") onFechar();
      if (evento.key === "ArrowLeft" && indice > 0) onIndice(indice - 1);
      if (evento.key === "ArrowRight" && indice < folhas.length - 1) onIndice(indice + 1);
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, [indice, folhas.length, onFechar, onIndice]);

  if (!folha) return null;
  return (
    <Modal role="dialog" aria-modal="true" aria-label={`Folha ${rotuloDaFolha(folha)} do lote ${folha.lote.rotulo}`}>
      <div className="barra">
        <span className="titulo">
          Lote {folha.lote.rotulo} · {rotuloDaFolha(folha)} · {indice + 1} de {folhas.length}
        </span>
        <button type="button" onClick={() => onIndice(indice - 1)} disabled={indice === 0} aria-label="Folha anterior">
          <FiChevronLeft size={18} aria-hidden="true" />
        </button>
        <button type="button" onClick={() => onIndice(indice + 1)} disabled={indice === folhas.length - 1} aria-label="Próxima folha">
          <FiChevronRight size={18} aria-hidden="true" />
        </button>
        <button type="button" ref={fechar} onClick={onFechar} aria-label="Fechar">
          <FiX size={18} aria-hidden="true" />
        </button>
      </div>
      <div className="palco" onClick={(e) => e.target === e.currentTarget && onFechar()} role="presentation">
        <div style={{ width: "100%", maxWidth: tamanhoDaFolha(folha).largura }}>
          <FolhaEscalada
            key={folha.chave}
            folha={folha}
            logo={logo}
            via={vias[`${folha.lote.chave}|${folha.setor.id}`]}
            celulasComAviso={celulasComAviso.get(folha.lote.chave)}
            ajuste={ajusteDe(folha)}
          />
        </div>
      </div>
    </Modal>
  );
}

/**
 * @param grupos [{ lote, folhas, avisos }] na ordem de saída
 * @param celulasComAviso Map lote.chave → Set "ambiente|coluna"
 */
export default function PreviaFolhas({ grupos, logo, vias, celulasComAviso, ajusteDe }) {
  const folhas = grupos.flatMap((grupo) => grupo.folhas);
  const [aberta, setAberta] = useState(null);
  const indiceDe = useCallback((folha) => folhas.findIndex((f) => f.chave === folha.chave), [folhas]);

  return (
    <Bloco aria-labelledby="titulo-previa">
      <header>
        <span className="passo" aria-hidden="true">3</span>
        <h2 id="titulo-previa">Prévia das folhas</h2>
      </header>
      {folhas.length === 0 && !grupos.some((grupo) => grupo.avisos.length > 0) ? (
        <Vazio>As folhas aparecem aqui assim que houver lote e setor marcados.</Vazio>
      ) : (
        <Previa>
          {grupos.filter((grupo) => grupo.folhas.length > 0 || grupo.avisos.length > 0).map(({ lote, folhas: doLote, avisos }) => (
            <GrupoDoLote key={lote.chave} aria-label={`Lote ${lote.rotulo}`}>
              <h3>
                Lote {lote.rotulo} <span>· {lote.cliente} · {doLote.length === 1 ? "1 folha" : `${doLote.length} folhas`}</span>
              </h3>
              {avisos.length > 0 && (
                <ListaDeAvisos aria-label="Avisos antes de imprimir">
                  {avisos.map((aviso) => <li key={aviso.mensagem}>{aviso.mensagem}</li>)}
                </ListaDeAvisos>
              )}
              <GradeDeMiniaturas>
                {doLote.map((folha) => (
                  <div key={folha.chave}>
                    <Miniatura
                      role="button"
                      tabIndex={0}
                      onClick={() => setAberta(indiceDe(folha))}
                      onKeyDown={(evento) => {
                        if (evento.key !== "Enter" && evento.key !== " ") return;
                        evento.preventDefault();
                        setAberta(indiceDe(folha));
                      }}
                      aria-label={`Abrir ${rotuloDaFolha(folha)} do lote ${lote.rotulo}`}
                    >
                      <FolhaEscalada
                        folha={folha}
                        logo={logo}
                        via={vias[`${lote.chave}|${folha.setor.id}`]}
                        celulasComAviso={celulasComAviso.get(lote.chave)}
                        ajuste={ajusteDe(folha)}
                      />
                    </Miniatura>
                    <LegendaDaMiniatura>
                      <span>{rotuloDaFolha(folha)}</span>
                      {vias[`${lote.chave}|${folha.setor.id}`]?.numero > 1 && (
                        <span>{vias[`${lote.chave}|${folha.setor.id}`].numero}ª via</span>
                      )}
                    </LegendaDaMiniatura>
                  </div>
                ))}
              </GradeDeMiniaturas>
            </GrupoDoLote>
          ))}
        </Previa>
      )}
      {aberta !== null && (
        <FolhaAmpliada
          folhas={folhas}
          indice={aberta}
          onIndice={setAberta}
          onFechar={() => setAberta(null)}
          logo={logo}
          vias={vias}
          celulasComAviso={celulasComAviso}
          ajusteDe={ajusteDe}
        />
      )}
    </Bloco>
  );
}
